import { IUseFetchOptionsParams } from '@common/api.models';
import { isValidApiBaseProps } from '@common/api.services';
import { FETCH_QUERIES_NAME, MUTATION_KEYS } from '@common/constants';
import { createUseFetchOptionsWithSearchFn } from '@common/selectOptions.services';
import { AxiosErrorResponse } from '@core/error.models';
import { isPositiveNumber } from '@core/Primitives/number.services';
import { IItemResponse, IPagedResultsV1 } from '@gcl/react-core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { IUpdateUserProfileRoleRequest, IUserProfileRole } from '@role/userprofileRole';
import {
  ICreateMySignatureRequest,
  ISignature,
  IUpdateMySignatureRequest,
  IUpdateUserProfileRequest,
  IUserProfile,
  IUserProfileOption,
} from './userProfile.models';
import {
  createMySignatureAsync,
  getMySignatureAsync,
  updateMySignatureAsync,
  IGetUserProfileOptionsByCompanyProps,
  getUserProfileOptionsByCompanyAsync,
  getSrpOptionsWithSearchAsync,
  getTwcOptionsWithSearchAsync,
  getTwcmOptionsWithSearchAsync,
  ISyncUserProfileRequest,
  syncUserProfileAsync,
  getGlobalRolesByUserProfileId,
  putUserProfilesGlobalRolesAsync,
  putUserProfileAsync,
  getUserProfileAsync,
  syncTrainingRecordsByUser,
  updateUserResignedStatusAsync,
} from './userProfile.services';
import { ISyncTrainingRecordsByUserRequest } from './training.models';

export const useCreateMySignatureAsync = () => {
  return useMutation<IItemResponse<ISignature>, AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.CREATE_MY_SIGNATURE],
    async (createSignatureRequest: ICreateMySignatureRequest) => {
      const result = await createMySignatureAsync(createSignatureRequest);
      return result;
    },
  );
};

export const useUpdateMySignatureAsync = () => {
  return useMutation<IItemResponse<ISignature>, AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.UPDATE_MY_SIGNATURE],
    async (updateSignatureRequest: IUpdateMySignatureRequest) => {
      const result = await updateMySignatureAsync(updateSignatureRequest);
      return result;
    },
  );
};

export const useGetMySignatureAsync = (enabled: boolean) => {
  return useQuery<IItemResponse<ISignature>, AxiosErrorResponse>(
    [FETCH_QUERIES_NAME.GET_MY_SIGNATURE],
    async () => {
      const result = await getMySignatureAsync();
      return result;
    },
    { enabled },
  );
};

export const useGetUserProfileOptionsByCompanyAsync = (
  { companyId, ...props }: IGetUserProfileOptionsByCompanyProps,
  enabled = true,
) => {
  return useQuery<IPagedResultsV1<IUserProfileOption>, AxiosErrorResponse>(
    [props.projectId, companyId, FETCH_QUERIES_NAME.USER_PROFILE_OPTIONS],
    async () => {
      const result = await getUserProfileOptionsByCompanyAsync({ companyId, ...props });
      return result;
    },
    { enabled: isPositiveNumber(companyId) && isValidApiBaseProps(props) && enabled },
  );
};

export const useFetchSrpOptionsWithSearch = ({
  isDirty = false,
  shouldConvertOptions = false,
}: IUseFetchOptionsParams) =>
  createUseFetchOptionsWithSearchFn({
    queryName: FETCH_QUERIES_NAME.SRP_OPTIONS,
    getOptionsWithSearchFn: getSrpOptionsWithSearchAsync,
    handleOptionsProps: {
      shouldConvertOptions,
      idFieldName: 'id',
      textFieldName: 'displayName',
    },
    enabled: !isDirty,
  });

export const useFetchTwcOptionsWithSearch = ({
  isDirty = false,
  shouldConvertOptions = false,
}: IUseFetchOptionsParams) =>
  createUseFetchOptionsWithSearchFn({
    queryName: FETCH_QUERIES_NAME.TWC_OPTIONS,
    getOptionsWithSearchFn: getTwcOptionsWithSearchAsync,
    handleOptionsProps: { shouldConvertOptions, idFieldName: 'id', textFieldName: 'displayName' },
    enabled: !isDirty,
  });

export const useFetchTwcmOptionsWithSearch = ({
  isDirty = false,
  shouldConvertOptions = false,
}: IUseFetchOptionsParams) =>
  createUseFetchOptionsWithSearchFn({
    queryName: FETCH_QUERIES_NAME.TWCM_OPTIONS,
    getOptionsWithSearchFn: getTwcmOptionsWithSearchAsync,
    handleOptionsProps: { shouldConvertOptions, idFieldName: 'id', textFieldName: 'displayName' },
    enabled: !isDirty,
  });

export const useSyncUserProfileAsync = () => {
  return useMutation<IUserProfile | undefined, AxiosErrorResponse, ISyncUserProfileRequest, unknown>(
    [MUTATION_KEYS.SYNC_USER_PROFILE],
    async (syncRequest: ISyncUserProfileRequest) => {
      const result = await syncUserProfileAsync(syncRequest);
      return result.item;
    },
  );
};

export const useUpdateGlobalRoles = () => {
  return useMutation<IUserProfile | undefined, AxiosErrorResponse, IUpdateUserProfileRoleRequest>(
    [MUTATION_KEYS.UPDATE_GLOBAL_ROLES],
    async (props: IUpdateUserProfileRoleRequest) => {
      const result = await putUserProfilesGlobalRolesAsync({ ...props, id: props.userProfile.id });
      return result.item;
    },
  );
};

export const useFetchGlobalRolesByUserProfileId = (id?: number, enable = true) =>
  useQuery<IUserProfileRole | undefined, AxiosErrorResponse>(
    [FETCH_QUERIES_NAME.GLOBAL_ROLES_BY_USER_PROFILE_ID, id],
    async () => {
      if (!id) return undefined;
      const result = await getGlobalRolesByUserProfileId({ userProfileId: id });
      return result.item;
    },
    {
      enabled: enable && isPositiveNumber(id),
    },
  );

export const useFetchUserProfile = (id?: number, enable = true) => {
  return useQuery<IUserProfile | undefined, AxiosErrorResponse>(
    [FETCH_QUERIES_NAME.USER_PROFILE, id],
    async () => {
      if (!id) return undefined;

      const results = await getUserProfileAsync(id);
      return results.item;
    },
    {
      enabled: !!id && enable,
    },
  );
};
export const useUpdateUserProfile = () => {
  return useMutation<IUserProfile | undefined, AxiosErrorResponse, IUpdateUserProfileRequest>(
    [MUTATION_KEYS.UPDATE_USER_PROFILE],
    async (props: IUpdateUserProfileRequest) => {
      const result = await putUserProfileAsync(props);
      return result.item;
    },
  );
};

export const useSyncTrainingRecordsByUser = () => {
  return useMutation<boolean, AxiosErrorResponse, ISyncTrainingRecordsByUserRequest>(
    [MUTATION_KEYS.SYNC_TRAINING_BY_USER],
    async (props: ISyncTrainingRecordsByUserRequest) => {
      const result = await syncTrainingRecordsByUser(props);
      return result;
    },
  );
};

export const useUpdateUserResignedStatus = () => {
  return useMutation<boolean, AxiosErrorResponse, { userId: number; isResigned: boolean }>(
    [MUTATION_KEYS.UPDATE_USER_RESIGNED_STATUS],
    async ({ userId, isResigned }) => {
      const result = await updateUserResignedStatusAsync(userId, isResigned);
      return result;
    },
  );
};
